import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type UseSwipeGestureOptions = {
  onSwipeDown?: () => void,
  onSwipeUp?: () => void,
  onSwipeMove?: (deltaY: number, progress: number) => void,
  onSwipeEnd?: () => void,
  threshold?: number,
  maxDistance?: number,
  enabled?: boolean,
};

export const useSwipeGesture = ({
  onSwipeDown,
  onSwipeUp,
  onSwipeMove,
  onSwipeEnd,
  threshold = 50,
  maxDistance = 150,
  enabled = true,
}: UseSwipeGestureOptions) => {
  const start = useRef<{ x: number, y: number } | null>(null);
  const [ isDragging, setIsDragging ] = useState(false);

  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    if (!enabled) return;

    start.current = {
      x: e.clientX,
      y: e.clientY,
    };
    setIsDragging(true);

    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [ enabled ]);

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    if (!start.current || !isDragging || !enabled) return;

    const dy = e.clientY - start.current.y;

    if (dy !== 0) {
      const progress = Math.min(Math.abs(dy) / maxDistance, 1);
      onSwipeMove?.(dy, progress);
    }
  }, [
    isDragging,
    enabled,
    maxDistance,
    onSwipeMove,
  ]);

  const onPointerUp = useCallback((e: ReactPointerEvent) => {
    if (!start.current || !enabled) return;

    const dy = e.clientY - start.current.y;

    if (dy > threshold) {
      onSwipeDown?.();
    } else if (dy < -threshold) {
      onSwipeUp?.();
    } else {
      onSwipeMove?.(0, 0);
    }

    onSwipeEnd?.();
    start.current = null;
    setIsDragging(false);

    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  }, [
    enabled,
    threshold,
    onSwipeDown,
    onSwipeUp,
    onSwipeMove,
    onSwipeEnd,
  ]);

  const onPointerCancel = useCallback((e: ReactPointerEvent) => {
    onSwipeMove?.(0, 0);
    onSwipeEnd?.();
    start.current = null;
    setIsDragging(false);
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  }, [ onSwipeMove, onSwipeEnd ]);

  useEffect(() => {
    if (!isDragging) return;

    const preventSelection = (e: Event) => e.preventDefault();
    document.addEventListener('selectstart', preventSelection);

    return () => {
      document.removeEventListener('selectstart', preventSelection);
    };
  }, [ isDragging ]);

  return {
    swipeHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      style: { touchAction: 'none' } as const,
    },
    isDragging,
  };
};
